/**
 * Vehicle Valuation — computation utilities
 *
 * All monetary values are stored as text in the DB.
 * This module provides default items and pure functions
 * for computing category totals, the total cost and the Cash price.
 */

export type ValuationCategory = 'vlc' | 'vpc' | 'cpc' | 'doc' | 'rfb' | 'config';

export interface ValuationItem {
  id?: number;
  category_id: number;
  key: string;
  value: string;
  is_default: boolean;
  sort_order: number;
}

export interface ValuationCategoryRecord {
  id: number;
  name: string;
  sort_order: number;
}

/** Short code for each category — used to map DB names to our internal codes */
const CATEGORY_CODES: Record<string, ValuationCategory> = {
  'Vehicle Landed Costs (VLC)': 'vlc',
  'Vehicle Preparation Costs (VPC)': 'vpc',
  'Compliance and Protection Costs (CPC)': 'cpc',
  'Driver Onboarding Costs (DOC)': 'doc',
  'Contingency and FX Protection (RFB)': 'rfb',
  'Summary': 'config',
};

export function getCategoryCode(categoryName: string): ValuationCategory {
  return CATEGORY_CODES[categoryName] ?? 'config';
}

/** Categories that contribute to the total cost (excludes Summary/config) */
const COST_CATEGORIES = new Set<ValuationCategory>(['vlc', 'vpc', 'cpc', 'doc', 'rfb']);

/**
 * Safely parse a text value to a number.
 * Returns 0 for non-numeric values (e.g. "Toyota Vitz 2020").
 */
export function parseValue(value: string): number {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
}

/**
 * Compute the total for a list of items.
 */
export function computeCategoryTotal(items: ValuationItem[]): number {
  return items.reduce((sum, item) => sum + parseValue(item.value), 0);
}

/**
 * Sum the items that sit in a cost category and pass `include`.
 * Summary items (primelane_cost_price, market_value, selling_price) never count.
 */
function sumCostItems(
  items: ValuationItem[],
  categories: ValuationCategoryRecord[],
  include: (item: ValuationItem) => boolean
): number {
  // Build a map of category_id → category_code
  const catMap = new Map<number, ValuationCategory>();
  for (const cat of categories) {
    catMap.set(cat.id, getCategoryCode(cat.name));
  }

  return items.reduce((sum, item) => {
    const code = catMap.get(item.category_id);
    if (code && COST_CATEGORIES.has(code) && include(item)) {
      return sum + parseValue(item.value);
    }
    return sum;
  }, 0);
}

/**
 * Compute the total cost.
 * Sum of all items in cost categories (VLC + VPC + CPC + DOC + RFB), the
 * first-year insurance included.
 * Excludes Summary items (primelane_cost_price, market_value, selling_price).
 */
export function computeTotalCost(
  items: ValuationItem[],
  categories: ValuationCategoryRecord[]
): number {
  return sumCostItems(items, categories, () => true);
}

/**
 * Compute the first-year insurance inside the total cost: every item in a cost
 * category whose key names insurance ("First year comprehensive insurance" by
 * default). An insurance-keyed item outside the cost categories is not in the
 * total, so it is not counted here either.
 */
export function computeValuationInsurance(
  items: ValuationItem[],
  categories: ValuationCategoryRecord[]
): number {
  return sumCostItems(items, categories, (item) =>
    item.key.toLowerCase().includes('insurance')
  );
}

/**
 * Compute the Cash price: the total cost less the first-year insurance inside
 * it. It is what the partner puts in; Primelane finances the Year 1 insurance
 * separately.
 */
export function computeCashPrice(
  items: ValuationItem[],
  categories: ValuationCategoryRecord[]
): number {
  return computeTotalCost(items, categories) - computeValuationInsurance(items, categories);
}

/**
 * Get the default valuation items.
 * These match the seed data in the migration and are used
 * when the DB is empty (first-load fallback).
 */
export function getDefaultItems(categoryId: number, categoryCode: ValuationCategory): ValuationItem[] {
  const defaults: Record<ValuationCategory, Array<{ key: string; value: string }>> = {
    vlc: [
      { key: 'Vehicle purchase price', value: '30000' },
      { key: 'Vanning Charges', value: '3800' },
      { key: 'Container (40ft)', value: '8400' },
      { key: 'Tax Bill', value: '26000' },
      { key: 'Other charges arising from port clearance', value: '5900' },
    ],
    vpc: [
      { key: 'Dashboard', value: '1000' },
      { key: 'Steering change', value: '2000' },
      { key: 'Plugs', value: '350' },
      { key: 'Battery', value: '950' },
      { key: 'AC', value: '200' },
      { key: 'Tyres', value: '1200' },
      { key: 'Oil + Filter + Workmanship', value: '350' },
      { key: 'Alignment + Washing', value: '80' },
      { key: 'Triangle + Fire extinguisher', value: '220' },
      { key: 'Fuel (Test, Vehicle registration)', value: '300' },
    ],
    cpc: [
      { key: 'Vehicle registration', value: '2800' },
      { key: 'Tracker device and installation', value: '1000' },
      { key: 'First year comprehensive insurance', value: '8379' },
      { key: 'Warranty costs', value: '2000' },
    ],
    doc: [
      { key: 'Fuel for driver address verification', value: '200' },
      { key: 'Referral fees', value: '300' },
    ],
    rfb: [
      { key: 'Risks and FX Buffer', value: '0' },
    ],
    config: [
      { key: 'Primelane cost price', value: '73900' },
      { key: 'Market value', value: '92000' },
      { key: 'Selling price', value: '92000' },
    ],
  };

  return (defaults[categoryCode] ?? []).map((item, index) => ({
    category_id: categoryId,
    key: item.key,
    value: item.value,
    is_default: true,
    sort_order: index,
  }));
}

/**
 * Format a number as GHS currency.
 */
export function formatGHS(amount: number): string {
  return new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
